import type { ErrorCopyFor } from "@amtana/browser";
import { phrase } from "@amtana/i18n/catalog";
import type { Catalogs, Translation } from "@amtana/i18n/catalog";
import type { SENDER_ERROR_CODES } from "@amtana/sender-contract";

// What a user reads when connecting or managing email accounts fails (packages/module ADR-001),
// in English and Spanish.

const EN = {
  notFoundTitle: "This email account no longer exists",
  notFoundDescription: "It may have been deleted. Open your email accounts to pick another.",
  limitReachedTitle: "You've reached the email account limit",
  limitReachedDescription: "Your plan has no room for more email accounts. Upgrade to add more.",
  duplicateTitle: "This email account is already connected",
  duplicateDescription: "Open it from your email accounts to change its settings.",
  verificationFailedTitle: "The server rejected the sending details",
  verificationFailedDescription: "Check the username, password and SMTP server, then save again.",
  warmupUpgradeTitle: "Warmup needs a Starter plan or above",
  warmupUpgradeDescription: "Upgrade your plan to warm up this account.",
  warmupManagedTitle: "Warmup is managed for this account",
  warmupManagedDescription: "The mailbox provider warms it up automatically.",
  invalidCsvTitle: "We couldn't read that CSV file",
  invalidCsvDescription: "Download the template, fill it in and upload it again.",
} as const;

const ES: Translation<typeof EN> = {
  notFoundTitle: "Esta cuenta de correo ya no existe",
  notFoundDescription: "Puede que se haya eliminado. Abre tus cuentas de correo para elegir otra.",
  limitReachedTitle: "Alcanzaste el límite de cuentas de correo",
  limitReachedDescription: "Tu plan no admite más cuentas de correo. Mejora tu plan para agregar más.",
  duplicateTitle: "Esta cuenta de correo ya está conectada",
  duplicateDescription: "Ábrela desde tus cuentas de correo para cambiar su configuración.",
  verificationFailedTitle: "El servidor rechazó los datos de envío",
  verificationFailedDescription: "Revisa el usuario, la contraseña y el servidor SMTP, y guarda de nuevo.",
  warmupUpgradeTitle: "El calentamiento requiere un plan Starter o superior",
  warmupUpgradeDescription: "Mejora tu plan para calentar esta cuenta.",
  warmupManagedTitle: "El calentamiento de esta cuenta es administrado",
  warmupManagedDescription: "El proveedor del buzón lo calienta de forma automática.",
  invalidCsvTitle: "No pudimos leer ese archivo CSV",
  invalidCsvDescription: "Descarga la plantilla, complétala y vuelve a subirla.",
};

export const SENDER_ERROR_CATALOGS: Catalogs<typeof EN> = { en: EN, es: ES };

export const SENDER_ERROR_COPY = {
  sender_not_found: {
    title: phrase(SENDER_ERROR_CATALOGS, "notFoundTitle"),
    description: phrase(SENDER_ERROR_CATALOGS, "notFoundDescription"),
  },
  sender_limit_reached: {
    title: phrase(SENDER_ERROR_CATALOGS, "limitReachedTitle"),
    description: phrase(SENDER_ERROR_CATALOGS, "limitReachedDescription"),
  },
  sender_duplicate: {
    title: phrase(SENDER_ERROR_CATALOGS, "duplicateTitle"),
    description: phrase(SENDER_ERROR_CATALOGS, "duplicateDescription"),
  },
  sender_verification_failed: {
    title: phrase(SENDER_ERROR_CATALOGS, "verificationFailedTitle"),
    description: phrase(SENDER_ERROR_CATALOGS, "verificationFailedDescription"),
  },
  sender_warmup_requires_upgrade: {
    title: phrase(SENDER_ERROR_CATALOGS, "warmupUpgradeTitle"),
    description: phrase(SENDER_ERROR_CATALOGS, "warmupUpgradeDescription"),
  },
  sender_warmup_managed: {
    title: phrase(SENDER_ERROR_CATALOGS, "warmupManagedTitle"),
    description: phrase(SENDER_ERROR_CATALOGS, "warmupManagedDescription"),
  },
  sender_invalid_csv: {
    title: phrase(SENDER_ERROR_CATALOGS, "invalidCsvTitle"),
    description: phrase(SENDER_ERROR_CATALOGS, "invalidCsvDescription"),
  },
} satisfies ErrorCopyFor<typeof SENDER_ERROR_CODES>;
